import { Fragment, useEffect, useState } from 'react'

import { isApolloError } from '@apollo/client'
import {
  Chip,
  Icon,
  Modal,
  ModalProvider,
  MultiValueTextField,
  PrimaryButton,
  SecondaryButton,
  TextButton,
  ValidationType,
  typography,
  useNotifications,
} from '@joor/design-system'
import truncate from 'lodash/truncate'
import styled from 'styled-components/macro'

import { useUserAnalytics } from 'utils/sdks/userAnalytics'
import { fromGlobalId } from 'utils/transformations/graphql'
import { isValidEmailAddress } from 'utils/validations'

import { EMPTY_CELL_CONTENT } from 'shop/products/utils'

import {
  useCreatePaymentInvoiceForOrders,
  useSendInvoicesForOrders,
} from './SendInvoiceModal.mutations'
import messages from 'containers/OrderList/OrderList.messages'

export const StyledModalContent = styled.div`
  padding: 0 10px;
  max-width: 800px;
  overflow: auto;
`

export const StyledInfo = styled.p`
  ${typography.alpha.primaryActive.body3}
  margin-top: 10px !important;
  margin-bottom: 16px !important;
`

const Table = styled.div`
  display: grid;
  white-space: nowrap;
  margin-bottom: 15px;
  &.one-link {
    grid-template-columns: repeat(3, auto);
  }
  &.several-links {
    grid-template-columns: repeat(3, auto) 80px;
    overflow: auto;
  }
`

const TableHeader = styled.h5`
  && {
    ${typography.alpha.primaryActive.body1}
    margin: 0;
    color: var(--color-primary-800);
    border-bottom: 1px solid var(--color-primary-200);
    padding-bottom: 4px;
  }
`

const MiniTableHeader = styled.h5`
  && {
    ${typography.alpha.primaryActive.body3}
    width: 100%;
    margin: 0;
    color: var(--color-primary-800);
    border-bottom: 1px solid var(--color-primary-200);
    padding-bottom: 4px;
    align-self: end;
  }
  &.right-centered {
    text-align: end;
  }
  &.link-header {
    padding-left: 20px;
  }
`

const TableCell = styled.div`
  ${typography.alpha.primaryActive.body3}
  color: var(--color-primary-800);
  padding: 5px 0;
  &.right-centered {
    text-align: end;
  }
  &.linkCells {
    padding-left: 20px;
    height: 25px;
    width: 90px;
  }
`

export const StyledEmailLinkWrapper = styled.div`
  display: flex;
  flex-direction: column;
  &.disabled {
    & > * {
      color: var(--color-primary-200);
    }

    & > div > div {
      color: var(--color-neutral-100);
    }
  }
`

export const StyledCCLinkLabel = styled.span`
  ${typography.alpha.primaryActive.body3}
  color: var(--color-primary-900);
`

export const StyledCaption = styled.span`
  ${typography.alpha.primaryActive.caption}
  color: var(--color-primary-900);
  &.error {
    color: var(--color-error-500);
  }
`

export const StyledModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  order: 3;
  gap: 15px;
  padding: 16px 24px 0 16px;
  margin-top: 5px;
  border-top: 1px solid var(--color-primary-200);
`

const StyledIcon = styled(Icon)`
  svg {
    --main-color: var(--color-warning-800);
  }
`

const WarningContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 8px 0;
  gap: 8px;
  width: 100%;
  border-top: 1px solid var(--color-primary-200);
`

const truncateName = (name: string): string =>
  truncate(name, {
    length: 50,
    separator: '...',
  })

export const TestIds = {
  IconWarnOnOrder: 'icon-warn-on-order',
  IconWarnOnFooter: 'icon-warn-on-footer',
} as const

export type InvoiceModalOrder = {
  id: string
  webOrderLookupId: string
  shippingEmail: string
  retailer: {
    id: string
  }
  retailerName: string
  referenceNumber: string
  brand: { id: string }
  billingAddress?: string
  retailerEmail?: string
  currency?: string
  amount?: string
}

type Props = {
  onClose: () => void
  selectedOrders: InvoiceModalOrder[]
  onSuccess?: (updatedIds: string[]) => void
}

const SendInvoiceModal = ({ onClose, selectedOrders, onSuccess }: Props) => {
  const [isEmailFieldInvalid, setIsEmailFieldInvalid] = useState(false)
  const [emailChips, setEmailChips] = useState<Chip[]>([])
  const [copiedLink, setCopiedLink] = useState<string>('')
  const [allLinksCopied, setAllLinksCopied] = useState<boolean>(false)
  const [copiedLinkOrderIds, setCopiedLinkOrderIds] = useState<string[]>([])
  const { addToasts } = useNotifications()
  const { sendInvoicesForOrders } = useSendInvoicesForOrders()
  const { createPaymentInvoiceForOrders } = useCreatePaymentInvoiceForOrders()
  const { trackEvent } = useUserAnalytics()
  const baseUrl = window.location.origin
  const orderIds = selectedOrders.map((order) => order.id)

  const selectedOrdersCount = selectedOrders.length
  const ordersWithEmail = selectedOrders.filter(
    (order) => order.shippingEmail && isValidEmailAddress(order.shippingEmail),
  )
  const areAllOrdersWithoutEmail = ordersWithEmail.length === 0
  const areSomeOrderWithoutEmail =
    selectedOrdersCount !== ordersWithEmail.length && !areAllOrdersWithoutEmail

  useEffect(() => {
    return () => {
      trackEvent('Order List: Send Payment Link(s) - Close')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAddLinkIds = (orderIds: string[]) => {
    setCopiedLinkOrderIds((prev) => {
      const newIds = orderIds.filter((id) => !prev.includes(id))
      return [...prev, ...newIds]
    })
  }

  const handleAddEmail = (newChip: Chip) => {
    if (emailChips.some(({ value }) => value === newChip.value)) return
    setEmailChips([...emailChips, newChip])
  }

  const handleRemoveEmail = (chipToDelete: Chip) => {
    setEmailChips(
      emailChips.filter((email) => email.value !== chipToDelete.value),
    )
  }

  const hasOneRetailer = selectedOrders.every(
    (order: InvoiceModalOrder) =>
      order.retailerName === selectedOrders[0].retailerName,
  )

  const isEmailFieldLengthInvalid =
    emailChips.map((chip) => chip.label).join().length > 250

  const sendInvoiceEmails = async () => {
    trackEvent('Order List: Send Payment Link(s) - Send Email', {
      orderIds: orderIds.map((id) => fromGlobalId(id).id),
    })

    const selectedOrdersPromises = ordersWithEmail.map((order) =>
      sendInvoicesForOrders(
        order,
        emailChips.map((chip) => chip.value),
      ).catch((err) => err),
    )
    const results = await Promise.all(selectedOrdersPromises)
    if (results.some((result) => isApolloError(result) || result?.errors)) {
      addToasts([
        {
          type: 'error',
          title: 'Unable to Send Emails',
          description:
            'An error has occurred. Please check if an email is associated with the selected order(s).',
          shouldCloseManually: true,
        },
      ])
    } else {
      addToasts([
        {
          type: 'success',
          title: 'Email(s) successful',
          description: 'Email(s) have been successfully sent.',
          shouldCloseManually: true,
        },
      ])
    }

    const updatedIds = results.reduce((acc, result) => {
      if (!isApolloError(result) && !result?.errors) {
        const ids = result?.data?.sendInvoicesForOrders.map(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (invoice: any) => invoice.orderId,
        )
        acc.push(...ids)
      }
      return acc
    }, [] as string[])

    if (updatedIds.length) {
      onSuccess?.(updatedIds)
    }

    onClose()
  }

  const copyInvoiceLinks = async () => {
    if (selectedOrders.length > 1) {
      trackEvent('Order List: Send Payment Link(s) - Copy Link (All)', {
        orderIds: orderIds.map((id) => fromGlobalId(id).id),
      })
    }
    const result = await createPaymentInvoiceForOrders(
      orderIds,
      selectedOrders[0].retailer.id,
    )
    if (result.errors) {
      addToasts([
        {
          type: 'error',
          title: 'Unable to copy link(s)',
          description: 'An unexpected server error has occurred. Try again.',
          shouldCloseManually: true,
        },
      ])
    } else {
      const urls = result?.data?.createInvoices.map(
        (result) => `${baseUrl}/invoices/pub/${result.invoiceId}`,
      )
      if (urls) {
        await navigator.clipboard.writeText(
          urls.length ? urls.join('\n') : urls[0],
        )
      }

      const updatedIds = result?.data?.createInvoices.map(
        (invoice) => invoice.orderId,
      )

      if (updatedIds?.length) {
        handleAddLinkIds(updatedIds)
      }

      setAllLinksCopied(true)
    }
  }

  const copyLink = async (orderId: string, retailerId: string) => {
    const result = await createPaymentInvoiceForOrders([orderId], retailerId)
    if (result.errors) {
      addToasts([
        {
          type: 'error',
          title: 'Unable to copy link',
          description: 'An unexpected server error has occurred. Try again.',
          shouldCloseManually: true,
        },
      ])
    } else {
      await navigator.clipboard.writeText(
        `${baseUrl}/invoices/pub/${result?.data?.createInvoices?.[0].invoiceId}`,
      )

      const updatedIds = result?.data?.createInvoices.map(
        (invoice) => invoice.orderId,
      )

      if (updatedIds?.length) {
        handleAddLinkIds(updatedIds)
      }

      setCopiedLink(orderId)
    }
  }

  const isLinkCopied = (orderId: string) => {
    return copiedLink === orderId
  }

  const getCopyLinkBtnText = () => {
    if (selectedOrders.length === 1) {
      return allLinksCopied ? 'Link Copied' : 'Copy Link'
    }
    return allLinksCopied ? 'All Links Copied' : 'Copy All Links'
  }

  const shouldShowWarning = areSomeOrderWithoutEmail || areAllOrdersWithoutEmail

  let warningMessage = ''

  if (areSomeOrderWithoutEmail) {
    warningMessage =
      'This order has been removed from your selection since there is no email associated to it.' +
      ' Hitting the “Send Email” button will only send a link for any orders with emails associated on the order.'
  } else if (areAllOrdersWithoutEmail) {
    warningMessage =
      'There are no emails associated with your selected orders.' +
      ' Email links cannot be sent through JOOR without adding an email to the order.'
  }

  return (
    <ModalProvider>
      <Modal
        isOpen
        onClose={() => {
          copiedLinkOrderIds.length && onSuccess?.(copiedLinkOrderIds)
          onClose()
        }}
        title={{
          text: messages.sendInvoice,
        }}
        size="medium"
      >
        <StyledModalContent>
          <StyledInfo>
            The payment link email will be sent from JOOR to the retailer email
            on the order. Retailer email address can be edited in the order
            directly. Copied links can be pasted into any email.
          </StyledInfo>
          <Table
            className={selectedOrders.length > 1 ? 'several-links' : 'one-link'}
          >
            <TableHeader>Retailer</TableHeader>
            <TableHeader>Email Address</TableHeader>
            <MiniTableHeader className="right-centered">PO #</MiniTableHeader>
            {selectedOrders.length > 1 && (
              <MiniTableHeader className="link-header">Link</MiniTableHeader>
            )}
            {selectedOrders.map((selectedOrder: InvoiceModalOrder) => (
              <Fragment key={selectedOrder.id}>
                <TableCell
                  title={selectedOrder.retailerName ?? EMPTY_CELL_CONTENT}
                >
                  {selectedOrder.retailerName
                    ? truncateName(selectedOrder.retailerName)
                    : EMPTY_CELL_CONTENT}
                </TableCell>
                <TableCell>
                  {!selectedOrder.shippingEmail ? (
                    <StyledIcon
                      iconName="warning"
                      iconSize="small"
                      plain
                      data-testid={TestIds.IconWarnOnOrder}
                    />
                  ) : (
                    selectedOrder.shippingEmail
                  )}
                </TableCell>
                <TableCell className="right-centered">
                  {selectedOrder.referenceNumber ?? EMPTY_CELL_CONTENT}
                </TableCell>
                {selectedOrders.length > 1 && (
                  <TableCell className="linkCells">
                    <TextButton
                      iconName={
                        isLinkCopied(selectedOrder.webOrderLookupId)
                          ? undefined
                          : 'link'
                      }
                      onClick={() => {
                        copyLink(selectedOrder.id, selectedOrder.retailer.id)
                        trackEvent(
                          'Order List: Send Payment Link(s) - Copy Link (Single)',
                          {
                            orderId: selectedOrder.webOrderLookupId,
                          },
                        )
                      }}
                    >
                      {isLinkCopied(selectedOrder.webOrderLookupId) ||
                      allLinksCopied
                        ? 'Copied'
                        : 'Copy'}
                    </TextButton>
                  </TableCell>
                )}
              </Fragment>
            ))}
          </Table>
          {shouldShowWarning && (
            <WarningContainer>
              <StyledIcon
                iconName="warning"
                iconSize="regular"
                plain
                data-testid={TestIds.IconWarnOnFooter}
              />
              <p>{warningMessage}</p>
            </WarningContainer>
          )}
          <StyledEmailLinkWrapper
            onClick={() => trackEvent('Order List: Send Payment Link(s) - CC')}
            className={hasOneRetailer ? '' : 'disabled'}
          >
            <StyledCCLinkLabel>BCC link to:</StyledCCLinkLabel>
            <MultiValueTextField
              errorMessage="Invalid Email Address"
              placeholder={
                emailChips.length
                  ? ''
                  : 'Press enter, space, or comma to save email'
              }
              value={emailChips}
              disabled={!hasOneRetailer || areAllOrdersWithoutEmail}
              onAddChip={handleAddEmail}
              onCloseChip={handleRemoveEmail}
              validation={ValidationType.Email}
              onValidate={(isValid) => {
                setIsEmailFieldInvalid(!isValid)
              }}
            />
            <StyledCaption className={isEmailFieldInvalid ? 'error' : ''}>
              250 Characters Max
            </StyledCaption>
          </StyledEmailLinkWrapper>
        </StyledModalContent>
        <StyledModalActions>
          {hasOneRetailer && (
            <SecondaryButton onClick={copyInvoiceLinks}>
              {getCopyLinkBtnText()}
            </SecondaryButton>
          )}
          <PrimaryButton
            disabled={
              isEmailFieldInvalid ||
              isEmailFieldLengthInvalid ||
              areAllOrdersWithoutEmail
            }
            onClick={sendInvoiceEmails}
          >
            Send email
          </PrimaryButton>
        </StyledModalActions>
      </Modal>
    </ModalProvider>
  )
}

export default SendInvoiceModal
